import { useState, MouseEvent, useEffect } from 'react';
import { ReactComponent as InfoIcon } from '../../../../assets/Icons/adornments/Info.svg';
import Popover from '@mui/material/Popover';
import { IconButton, useTheme } from '@mui/material';
import { StandardizerRecord } from '../../../../api/models';
import { getStageColor } from '../../../../utils/formattingFns';

export default function InfoPopover({
    record,
}: {
    record: StandardizerRecord,
}) {
    const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
    const theme = useTheme();

    const [ resultColors, setResultColors ] = useState<{
        label: string,
        background: string
    }>({
        label: '#006D77',
        background: '#D5E7E8',
    });

    const [ severityColors, setSeverityColors ] = useState<{
        label: string,
        background: string
    }>({
        label: '#006D77',
        background: '#D5E7E8',
    });

    const [ transformedFields, setTransformedFields ] = useState<{
        type: 'added' | 'changed',
        key: string,
        originalValue: string,
        newValue: string,
    }[]>([]);

    useEffect(() => {
        if (record.result) {
            const { label, background } = getStageColor(record.result);
            setResultColors({ label, background });
        }

        if (record.severity) {
            const { label, background } = getStageColor(record.severity);
            setSeverityColors({ label, background });
        }

        const trFields: {
            type: 'added' | 'changed',
            key: string,
            originalValue: string,
            newValue: string,
        }[] = [];

        const original = record.original_fields;
        const transformed = record.transformed_fields;

        if (original && transformed && Object.keys(transformed).length > 0) {
            const ogKeys = Object.keys(original);
            for (const [key, value] of Object.entries(transformed)) {
                let field: {
                    type: 'added' | 'changed',
                    key: string,
                    originalValue: string,
                    newValue: string,
                } = {
                    type: 'changed',
                    key,
                    originalValue: '',
                    newValue: value !== null && value !== undefined ? value.toString() : '',
                }
                if (key === 'SMILES_dataset_structure') {
                    const ogSmilesKey = ogKeys.find(ogKey => ogKey === 'SMILES_datafile_structure');
                    if (ogSmilesKey) {
                        field.key = 'SMILES_datafile_structure';
                        field.originalValue = original[ogSmilesKey] as string;
                    }
                } else {
                    const matchingKey = ogKeys.find(ogKey => ogKey === key);
                    if (matchingKey) {
                        field.originalValue = original[matchingKey] as string;
                    } else {
                        field.type = 'added';
                    }
                }

                field.originalValue = field.originalValue !== undefined && field.originalValue !== null ? field.originalValue.toString() : '';

                if (field.type === 'added' || (field.newValue !== field.originalValue)) {
                    trFields.push(field);
                }
               
            }
        };
        

        setTransformedFields(trFields);

    }, [record]);

    const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const open = Boolean(anchorEl);
    const id = open ? 'protocol-tooltip-popover' : undefined;

    return (
        <div>
            <IconButton 
                onClick={handleClick} 
                size='small'
                sx={{
                    color: '#9292AA',
                    '&:hover': {
                        color: theme.palette.primary.main,
                    }
                }}
            >
                <InfoIcon className='w-[16px]'/>
            </IconButton>
            <Popover
                id={id}
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                }}
                slotProps={{
                    paper: {
                        style: {
                            borderRadius: '10px',
                        }
                    }
                }}
            >
                <div className='p-6 max-h-[400px] overflow-y-auto'>
                    <div className="grid grid-cols-[auto_1fr] auto-rows-auto gap-x-3 gap-y-5">
                        <p className='text-default font-semibold'>Result:</p>
                        <div className='flex justify-start'>
                            <div 
                                style={{ color: resultColors.label, backgroundColor: resultColors.background }} 
                                className="py-1 px-3 rounded-lg font-semibold text-[12px] text-nowrap capitalize"
                            >
                                {record.result ? record.result : ''}
                            </div>
                        </div>
                        

                        <p className='text-default font-semibold'>Severity:</p>
                        <div className='flex justify-start'>
                            <div 
                                style={{ color: severityColors.label, backgroundColor: severityColors.background }} 
                                className="py-1 px-3 rounded-lg font-semibold text-[12px] text-nowrap capitalize"
                            >
                                {record.severity ? record.severity : ''}
                            </div>
                        </div>

                        {transformedFields.length > 0 && (
                            <>
                                <p className='text-default font-semibold'>Changes:</p>
                                <div className='grid grid-cols-[auto_1fr] auto-rows-auto gap-x-3 gap-y-3'>
                                    {transformedFields.map(field => {
                                        if (field.type === 'added') {
                                            return (
                                                <>
                                                    <p className='font-semibold'><span className='text-[#7BC960]'>+</span>{field.key}</p>
                                                    <p></p>
                                                </>
                                            )
                                        } else {
                                            return (
                                                <>
                                                    <p className='font-semibold'>{field.key}</p>
                                                    <div className='flex justify-start items-center gap-2 max-w-[200px] flex-wrap'>
                                                        <p className={`${field.key.toLocaleLowerCase().includes('smiles') && 'break-all'}`}>{field.originalValue}</p>
                                                        <p>&#8594;</p>
                                                        <p className={`${field.key.toLocaleLowerCase().includes('smiles') && 'break-all'}`}>{field.newValue}</p>
                                                    </div>
                                                </>
                                            )
                                        }
                                    })}
                                </div>
                            </>
                        )}
                        
                        <p className='text-default font-semibold'>Reason:</p>
                        <pre className='whitespace-pre-wrap max-w-[300px] text-[14px] font-[Montserrat]'>
                            {record.reason ?? ''}
                        </pre>
                    </div>
                </div>
                    
            </Popover>
        </div>
    )
}